import { Component } from '@angular/core';
import {
	AbstractControl,
	FormArray,
	FormBuilder,
	FormGroup,
	Validators,
} from '@angular/forms';
import { Router } from '@angular/router';
import { CdkDragDrop } from '@angular/cdk/drag-drop';

import { HttpDynamicService } from '../../../service/http-dynamic.service';
import { HttpWorkflowService } from '../../../service/http-workflow.service';
import {
	DynamicEntityDto,
	DynamicPropertyDto,
	WorkflowDto,
	WorkflowStepDto,
	WorkflowStepStatusDto,
	WorkflowStepSectionDto,
	WorkflowStepSectionElementDto,
} from '../../../service/dto';

@Component({
	selector: 'app-dynamic-form-builder',
	standalone: false,
	templateUrl: './dynamic-form-builder.component.html',
	styleUrl: './dynamic-form-builder.component.scss',
})
export class DynamicFormBuilderComponent {
	readonly WORKFLOW_ENTITY_ID = -1;
	form: FormGroup;

	dynamicEntities: DynamicEntityDto[] = [];
	workflows: WorkflowDto[] = [];

	selectedWorkflow?: WorkflowDto;

	workflowSteps: WorkflowStepDto[] = [];

	workflowStatuses: WorkflowStepStatusDto[] = [];

	entityProperties: {
		[entityId: number]: DynamicPropertyDto[];
	} = {};

	constructor(
		private fb: FormBuilder,
		private router: Router,
		private dynamicService: HttpDynamicService,
		private workflowService: HttpWorkflowService,
	) {
		this.form = this.fb.group({
			name: ['', Validators.required],

			description: [''],

			workflowId: [null, Validators.required],

			workflowStepId: [null, Validators.required],

			workflowStatusId: [null],

			formLogo: this.fb.group({
				fileName: [''],
				dataUrl: [''],
			}),

			sections: new FormArray<FormGroup>([]),
		});

		this.initialiseWorkflowProperties();
		this.loadDynamicEntities();
		this.loadWorkflows();
	}

	private getProperty(
		entityId: number | null,
		propertyId: number | null,
	): DynamicPropertyDto | undefined {
		if (
			entityId === null ||
			entityId === undefined ||
			propertyId === null ||
			propertyId === undefined
		) {
			return undefined;
		}

		return (this.entityProperties[entityId] ?? []).find(
			(property) => property.id === propertyId,
		);
	}

	private mapDataTypeToFieldType(dataType: string | undefined): string {
		const type = (dataType ?? '').toLowerCase().trim();

		switch (type) {
			case 'number':
				return 'Number';

			case 'date':
			case 'pastdate':
			case 'futuredate':
			case 'pastdatetime':
			case 'futuredatetime':
				return 'Date';

			case 'boolean':
			case 'requiredcheckbox':
			case 'checkboxwithstaticvalue':
				return 'Checkbox';

			case 'textarea':
				return 'Textarea';

			case 'dropdownsingle':
			case 'selectfromrecords':
			case 'dependantlist':
			case 'memberanddependantlist':
			case 'hierarchy':
			case 'icd10':
			case 'serviceproviderid':
				return 'Dropdown';

			case 'file':
				return 'File';

			default:
				return 'Text';
		}
	}

	private applyPropertyToField(
		field: FormGroup,
		entityId: number | null,
		propertyId: number | null,
		applyRequired: boolean = false,
	): void {
		const property = this.getProperty(entityId, propertyId);

		if (!property) {
			return;
		}

		// Automatically determine field type
		const fieldType = this.mapDataTypeToFieldType(property.dataType);

		field.get('type')?.setValue(fieldType, { emitEvent: false });

		// Automatically set label,
		// but only if user has not renamed it
		const currentLabel = field.get('label')?.value ?? '';

		if (!currentLabel || /^Field \d+$/i.test(currentLabel)) {
			field
				.get('label')
				?.setValue(property.name || property.property || currentLabel, {
					emitEvent: false,
				});
		}

		// Automatically use property description as help text,
		// but do not overwrite custom help text
		const currentHelpText = field.get('helpText')?.value ?? '';

		if (!currentHelpText && property.description) {
			field
				.get('helpText')
				?.setValue(property.description, { emitEvent: false });
		}

		// Required should come from the TARGET property,
		// not from an Output-only Source property
		if (applyRequired) {
			field.patchValue(
				{
					required: property.required === true,

					minValue: property.minValue ?? null,

					maxValue: property.maxValue ?? null,

					regex: property.regex ?? '',

					errorMessageRequired: property.errorMessageRequired ?? '',

					errorMessageRegex: property.errorMessageRegex ?? '',

					errorMessageMinValue: property.errorMessageMinValue ?? '',

					errorMessageMaxValue: property.errorMessageMaxValue ?? '',
				},
				{
					emitEvent: false,
				},
			);
		}
	}

	loadDynamicEntities(): void {
		this.dynamicService.getAll().subscribe((result) => {
			this.dynamicEntities = result;
		});
	}

	loadEntityProperties(entityId: number | null): void {
		if (entityId === null || entityId === undefined) {
			return;
		}

		// Workflow properties already exist locally
		if (entityId === this.WORKFLOW_ENTITY_ID) {
			return;
		}

		// Already loaded
		if (this.entityProperties[entityId]) {
			return;
		}

		this.dynamicService.get(entityId).subscribe((result) => {
			this.entityProperties[entityId] = (result.properties ?? []).filter(
				(property) => property.fieldDisabled == false,
			);
		});
	}
	loadWorkflows(): void {
		this.workflowService.getAll().subscribe((result) => {
			this.workflows = [...(result ?? [])].sort((a, b) =>
				(a.label || a.name || '').localeCompare(
					b.label || b.name || '',
				),
			);
		});
	}

	getEntityLabel(entity: DynamicEntityDto): string {
		const item = entity as any;

		return (
			item.name ??
			item.label ??
			item.entityName ??
			item.code ??
			`Entity ${item.id}`
		);
	}

	getEntityLabelById(entityId: number | null | undefined): string {
		if (!entityId) {
			return '';
		}

		if (entityId === this.WORKFLOW_ENTITY_ID) {
			return 'Workflow';
		}

		const entity = this.dynamicEntities.find(
			(item) => item.id === entityId,
		);

		if (!entity) {
			return `Entity ${entityId}`;
		}

		return this.getEntityLabel(entity);
	}

	getPropertyLabel(property: DynamicPropertyDto): string {
		return property.name || property.property || `Property ${property.id}`;
	}

	getPropertyLabelById(
		entityId: number | null | undefined,
		propertyId: number | null | undefined,
	): string {
		if (!entityId || !propertyId) {
			return '';
		}

		const properties = this.entityProperties[entityId] ?? [];

		const property = properties.find((item) => item.id === propertyId);

		if (!property) {
			return `Property ${propertyId}`;
		}

		return this.getPropertyLabel(property);
	}

	get sections(): FormArray {
		return this.form.get('sections') as FormArray;
	}

	addSection(): void {
		const section = this.fb.group({
			workflowSectionId: [null],

			name: [`Section ${this.sections.length + 1}`, Validators.required],

			description: [''],

			sectionOrder: [this.sections.length + 1],

			statusIds: [this.workflowStatuses.map((status) => status.id)],

			fields: new FormArray<FormGroup>([]),
		});

		this.sections.push(section);
	}

	removeSection(index: number): void {
		this.sections.removeAt(index);
	}

	dropSection(event: CdkDragDrop<any[]>): void {
		if (event.previousIndex === event.currentIndex) {
			return;
		}

		const section = this.sections.at(event.previousIndex);

		this.sections.removeAt(event.previousIndex);

		this.sections.insert(event.currentIndex, section);
	}

	getFields(sectionIndex: number): FormArray {
		return this.sections.at(sectionIndex).get('fields') as FormArray;
	}

	addField(sectionIndex: number): void {
		const fields = this.getFields(sectionIndex);

		const field = this.fb.group({
			// Basic field configuration
			label: [`Field ${fields.length + 1}`, Validators.required],

			type: ['Text', Validators.required],

			direction: ['Input', Validators.required],

			width: [24, Validators.required],

			// Behaviour
			required: [false],
			readOnly: [false],

			// Display
			placeholder: [''],
			helpText: [''],

			minValue: [null],
			maxValue: [null],
			regex: [''],

			errorMessageRequired: [''],
			errorMessageRegex: [''],
			errorMessageMinValue: [''],
			errorMessageMaxValue: [''],

			options: this.fb.control([]),

			signatureData: [''],

			// Output / Source mapping
			sourceEntityId: [null],
			sourcePropertyId: [null],

			// Input / Target mapping
			targetEntityId: [null],
			targetPropertyId: [null],

			workflowElementId: [null],

			workflowNative: [true],

			lookupKey: [''],

			workflowDataType: ['Text'],

			orderIndex: [fields.length + 1],

			lineNumber: [null],

			dependencyId: [null],

			dependencyValue: [''],

			valueWhenChecked: [''],

			unique: [false],

			hidden: [false],

			flagForDuplicateTicket: [false],

			flagForSpecialField: [false],

			statusIds: [this.workflowStatuses.map((status) => status.id)],
		});

		this.setupFieldListeners(field);

		fields.push(field);
	}
	onWorkflowChanged(workflowId: number | null): void {
		this.selectedWorkflow = undefined;

		this.workflowSteps = [];
		this.workflowStatuses = [];

		this.form.patchValue(
			{
				workflowStepId: null,
				workflowStatusId: null,
			},
			{
				emitEvent: false,
			},
		);

		if (workflowId === null || workflowId === undefined) {
			return;
		}

		this.workflowService.get(workflowId).subscribe((workflow) => {
			this.selectedWorkflow = workflow;

			this.workflowSteps = [...(workflow.steps ?? [])]
				.filter((step) => step.questionnaire !== true)
				.sort((a, b) => (a.stepOrder ?? 0) - (b.stepOrder ?? 0));
		});
	}

	onWorkflowStepChanged(stepId: number | null): void {
		this.workflowStatuses = [];

		this.form.patchValue(
			{
				workflowStatusId: null,
			},
			{
				emitEvent: false,
			},
		);

		if (stepId === null || stepId === undefined) {
			this.sections.clear();
			return;
		}

		const step = this.workflowSteps.find((item) => item.id === stepId);

		if (!step) {
			this.sections.clear();
			return;
		}

		this.workflowStatuses = [...(step.statuses ?? [])];

		this.form.patchValue(
			{
				name: step.label ?? '',
				description: step.description ?? '',
			},
			{
				emitEvent: false,
			},
		);

		this.loadWorkflowStepIntoBuilder(step);
	}
	onLogoSelected(event: Event): void {
		const input = event.target as HTMLInputElement;

		const file = input.files?.[0];

		if (!file) {
			return;
		}

		if (!file.type.startsWith('image/')) {
			input.value = '';
			return;
		}

		const reader = new FileReader();

		reader.onload = () => {
			const formLogo = this.form.get('formLogo') as FormGroup;

			formLogo.patchValue({
				fileName: file.name,
				dataUrl: reader.result as string,
			});
		};

		reader.readAsDataURL(file);
	}

	clearLogo(input?: HTMLInputElement): void {
		const formLogo = this.form.get('formLogo') as FormGroup;

		formLogo.patchValue({
			fileName: '',
			dataUrl: '',
		});

		if (input) {
			input.value = '';
		}
	}

	private setupFieldListeners(field: FormGroup): void {
		const sourceEntity = field.get('sourceEntityId');

		const sourceProperty = field.get('sourcePropertyId');

		const targetEntity = field.get('targetEntityId');

		const targetProperty = field.get('targetPropertyId');

		sourceProperty?.valueChanges.subscribe((propertyId: number | null) => {
			const entityId = sourceEntity?.value;

			this.applyPropertyToField(field, entityId, propertyId, false);
		});

		targetProperty?.valueChanges.subscribe((propertyId: number | null) => {
			const entityId = targetEntity?.value;

			const direction = field.get('direction')?.value;

			if (direction === 'InputOutput' && sourceProperty?.value) {
				const property = this.getProperty(entityId, propertyId);

				if (property) {
					field
						.get('required')
						?.setValue(property.required === true, {
							emitEvent: false,
						});
				}

				return;
			}

			this.applyPropertyToField(field, entityId, propertyId, true);
		});

		sourceEntity?.valueChanges.subscribe((entityId: number | null) => {
			sourceProperty?.setValue(null, { emitEvent: false });

			this.loadEntityProperties(entityId);
		});

		targetEntity?.valueChanges.subscribe((entityId: number | null) => {
			targetProperty?.setValue(null, { emitEvent: false });

			this.loadEntityProperties(entityId);
		});

		field.get('direction')?.valueChanges.subscribe((direction: string) => {
			this.applyBindingValidators(field, direction);
		});

		this.applyBindingValidators(field, field.get('direction')?.value);

		field.get('type')?.valueChanges.subscribe((type: string) => {
			if (type === 'Signature') {
				field.patchValue(
					{
						direction: 'Input',

						sourceEntityId: null,
						sourcePropertyId: null,

						targetEntityId: null,
						targetPropertyId: null,
					},
					{
						emitEvent: false,
					},
				);
			}

			this.applyBindingValidators(field, field.get('direction')?.value);
		});
	}

	private applyBindingValidators(field: FormGroup, direction: string): void {
		const sourceEntity = field.get('sourceEntityId');

		const sourceProperty = field.get('sourcePropertyId');

		const targetEntity = field.get('targetEntityId');

		const targetProperty = field.get('targetPropertyId');

		// Clear old validators first
		sourceEntity?.clearValidators();
		sourceProperty?.clearValidators();

		targetEntity?.clearValidators();
		targetProperty?.clearValidators();

		// Existing/new Workflow fields already have their own
		// native PANDA storage and do not require an
		// external Source or Target mapping.
		if (field.get('workflowNative')?.value === true) {
			sourceEntity?.updateValueAndValidity({
				emitEvent: false,
			});

			sourceProperty?.updateValueAndValidity({
				emitEvent: false,
			});

			targetEntity?.updateValueAndValidity({
				emitEvent: false,
			});

			targetProperty?.updateValueAndValidity({
				emitEvent: false,
			});

			return;
		}

		// INPUT
		if (direction === 'Input') {
			targetEntity?.setValidators(Validators.required);

			targetProperty?.setValidators(Validators.required);

			field.patchValue(
				{
					sourceEntityId: null,
					sourcePropertyId: null,
				},
				{ emitEvent: false },
			);
		}

		// OUTPUT
		if (direction === 'Output') {
			sourceEntity?.setValidators(Validators.required);

			sourceProperty?.setValidators(Validators.required);

			field.patchValue(
				{
					targetEntityId: null,
					targetPropertyId: null,
				},
				{ emitEvent: false },
			);
		}

		// INPUT + OUTPUT
		if (direction === 'InputOutput') {
			sourceEntity?.setValidators(Validators.required);

			sourceProperty?.setValidators(Validators.required);

			targetEntity?.setValidators(Validators.required);

			targetProperty?.setValidators(Validators.required);
		}

		sourceEntity?.updateValueAndValidity({
			emitEvent: false,
		});

		sourceProperty?.updateValueAndValidity({
			emitEvent: false,
		});

		targetEntity?.updateValueAndValidity({
			emitEvent: false,
		});

		targetProperty?.updateValueAndValidity({
			emitEvent: false,
		});

		// Signature is native to Dynamic Forms.
		// It does not require PANDA data binding.
		if (field.get('type')?.value === 'Signature') {
			field.patchValue(
				{
					sourceEntityId: null,
					sourcePropertyId: null,

					targetEntityId: null,
					targetPropertyId: null,
				},
				{
					emitEvent: false,
				},
			);

			sourceEntity?.updateValueAndValidity({
				emitEvent: false,
			});

			sourceProperty?.updateValueAndValidity({
				emitEvent: false,
			});

			targetEntity?.updateValueAndValidity({
				emitEvent: false,
			});

			targetProperty?.updateValueAndValidity({
				emitEvent: false,
			});

			return;
		}
	}

	removeField(sectionIndex: number, fieldIndex: number): void {
		this.getFields(sectionIndex).removeAt(fieldIndex);
	}

	dropField(sectionIndex: number, event: CdkDragDrop<any[]>): void {
		if (event.previousIndex === event.currentIndex) {
			return;
		}

		const fields = this.getFields(sectionIndex);

		const field = fields.at(event.previousIndex);

		fields.removeAt(event.previousIndex);

		fields.insert(event.currentIndex, field);
	}

	private activeSignatureCanvas?: HTMLCanvasElement;

	private isSignatureDrawing = false;

	getPreviewColumnClass(width: number): string {
		switch (width) {
			case 24:
				return 'col-12';

			case 12:
				return 'col-6';

			case 8:
				return 'col-4';

			case 6:
				return 'col-3';

			default:
				return 'col-12';
		}
	}

	doCancel(): void {
		this.router.navigate(['/form/list']);
	}

	doSave(): void {
		if (this.form.invalid) {
			return;
		}

		console.log('Form:', this.form.getRawValue());
	}

	private initialiseWorkflowProperties(): void {
		this.entityProperties[this.WORKFLOW_ENTITY_ID] = [
			{
				id: -101,
				name: 'Workflow ID',
				property: 'id',
				dataType: 'Number',
				fieldDisabled: false,
			} as DynamicPropertyDto,

			{
				id: -102,
				name: 'Workflow Name',
				property: 'name',
				dataType: 'Text',
				fieldDisabled: false,
			} as DynamicPropertyDto,

			{
				id: -103,
				name: 'Workflow Label',
				property: 'label',
				dataType: 'Text',
				fieldDisabled: false,
			} as DynamicPropertyDto,

			{
				id: -104,
				name: 'Workflow Description',
				property: 'description',
				dataType: 'Text',
				fieldDisabled: false,
			} as DynamicPropertyDto,

			{
				id: -105,
				name: 'Ticket Mode',
				property: 'ticketMode',
				dataType: 'Text',
				fieldDisabled: false,
			} as DynamicPropertyDto,

			{
				id: -106,
				name: 'Reference Prefix',
				property: 'referencePrefix',
				dataType: 'Text',
				fieldDisabled: false,
			} as DynamicPropertyDto,
		];
	}

	startSignature(
		event: MouseEvent | TouchEvent,
		field: AbstractControl,
	): void {
		if (this.isSignatureDisabled(field)) {
			return;
		}

		event.preventDefault();

		const canvas = event.currentTarget as HTMLCanvasElement;

		const ctx = canvas.getContext('2d');

		if (!ctx) {
			return;
		}

		this.configureSignatureContext(ctx);

		const position = this.getSignaturePosition(event, canvas);

		this.activeSignatureCanvas = canvas;

		this.isSignatureDrawing = true;

		ctx.beginPath();

		ctx.moveTo(position.x, position.y);
	}

	drawSignature(
		event: MouseEvent | TouchEvent,
		field: AbstractControl,
	): void {
		if (
			!this.isSignatureDrawing ||
			!this.activeSignatureCanvas ||
			this.isSignatureDisabled(field)
		) {
			return;
		}

		const canvas = event.currentTarget as HTMLCanvasElement;

		if (canvas !== this.activeSignatureCanvas) {
			return;
		}

		event.preventDefault();

		const ctx = canvas.getContext('2d');

		if (!ctx) {
			return;
		}

		this.configureSignatureContext(ctx);

		const position = this.getSignaturePosition(event, canvas);

		ctx.lineTo(position.x, position.y);

		ctx.stroke();
	}

	stopSignature(field: AbstractControl): void {
		if (!this.isSignatureDrawing || !this.activeSignatureCanvas) {
			return;
		}

		this.isSignatureDrawing = false;

		const dataUrl = this.activeSignatureCanvas.toDataURL('image/png');

		field.get('signatureData')?.setValue(dataUrl, { emitEvent: false });

		this.activeSignatureCanvas = undefined;
	}

	clearSignature(canvas: HTMLCanvasElement, field: AbstractControl): void {
		const ctx = canvas.getContext('2d');

		if (ctx) {
			ctx.clearRect(0, 0, canvas.width, canvas.height);
		}

		field.get('signatureData')?.setValue('', { emitEvent: false });
	}

	private configureSignatureContext(ctx: CanvasRenderingContext2D): void {
		ctx.lineWidth = 2;
		ctx.lineCap = 'round';
		ctx.lineJoin = 'round';
		ctx.strokeStyle = '#000000';
	}

	private isSignatureDisabled(field: AbstractControl): boolean {
		return (
			field.get('readOnly')?.value === true ||
			field.get('direction')?.value === 'Output'
		);
	}

	private getSignaturePosition(
		event: MouseEvent | TouchEvent,
		canvas: HTMLCanvasElement,
	): {
		x: number;
		y: number;
	} {
		const rect = canvas.getBoundingClientRect();

		let clientX = 0;
		let clientY = 0;

		if (event instanceof TouchEvent) {
			const touch = event.touches[0] || event.changedTouches[0];

			if (touch) {
				clientX = touch.clientX;
				clientY = touch.clientY;
			}
		} else {
			clientX = event.clientX;
			clientY = event.clientY;
		}

		const scaleX = canvas.width / rect.width;

		const scaleY = canvas.height / rect.height;

		return {
			x: (clientX - rect.left) * scaleX,

			y: (clientY - rect.top) * scaleY,
		};
	}
	private loadWorkflowStepIntoBuilder(step: WorkflowStepDto): void {
		this.sections.clear();

		const workflowSections = [...(step.sections ?? [])].sort(
			(a, b) => (a.sectionOrder ?? 0) - (b.sectionOrder ?? 0),
		);

		workflowSections.forEach((section) => {
			this.sections.push(this.createSectionFromWorkflow(section));
		});
	}

	private createSectionFromWorkflow(
		input: WorkflowStepSectionDto,
	): FormGroup {
		const fields = new FormArray<FormGroup>([]);

		const section = this.fb.group({
			workflowSectionId: [input.id],

			name: [input.label ?? '', Validators.required],

			description: [''],

			sectionOrder: [input.sectionOrder ?? 0],

			statusIds: [input.statusIds ?? []],

			fields,
		});

		const elements = [...(input.elements ?? [])].sort(
			(a, b) => (a.orderIndex ?? 0) - (b.orderIndex ?? 0),
		);

		elements.forEach((element) => {
			fields.push(this.createFieldFromWorkflow(element));
		});

		return section;
	}
	private createFieldFromWorkflow(
		input: WorkflowStepSectionElementDto,
	): FormGroup {
		const type = this.getWorkflowElementBuilderType(input);

		const field = this.fb.group({
			// Existing Workflow identity
			workflowElementId: [input.id],

			workflowNative: [true],

			lookupKey: [input.lookupKey ?? ''],

			workflowDataType: [input.dataType],

			orderIndex: [input.orderIndex ?? 0],

			lineNumber: [input.lineNumber ?? null],

			// Builder configuration
			label: [input.label ?? '', Validators.required],

			type: [type, Validators.required],

			direction: ['Input', Validators.required],

			width: [24, Validators.required],

			required: [input.required === true],

			readOnly: [false],

			placeholder: [''],

			helpText: [input.description ?? ''],

			minValue: [input.minValue ?? null],

			maxValue: [input.maxValue ?? null],

			regex: [input.regex ?? ''],

			errorMessageRequired: [input.errorMessageRequired ?? ''],

			errorMessageRegex: [input.errorMessageRegex ?? ''],

			errorMessageMinValue: [input.errorMessageMinValue ?? ''],

			errorMessageMaxValue: [input.errorMessageMaxValue ?? ''],

			options: this.fb.control([...(input.options ?? [])]),

			signatureData: [''],

			// Existing workflow behaviour
			dependencyId: [input.dependencyId ?? null],

			dependencyValue: [input.dependencyValue ?? ''],

			valueWhenChecked: [input.valueWhenChecked ?? ''],

			unique: [input.unique === true],

			hidden: [input.hidden === true],

			flagForDuplicateTicket: [input.flagForDuplicateTicket === true],

			flagForSpecialField: [input.flagForSpecialField === true],

			statusIds: [input.statusIds ?? []],

			// Optional external data bindings
			sourceEntityId: [null],

			sourcePropertyId: [null],

			targetEntityId: [null],

			targetPropertyId: [null],
		});

		this.setupFieldListeners(field);

		return field;
	}
	private getWorkflowElementBuilderType(
		input: WorkflowStepSectionElementDto,
	): string {
		const lookupKey = (input.lookupKey ?? '').toLowerCase().trim();

		if (
			input.flagForSpecialField === true &&
			lookupKey.startsWith('signature.')
		) {
			return 'Signature';
		}

		return this.mapDataTypeToFieldType(input.dataType);
	}
}
